import * as React from "react";
import { UserProfile } from "../../models/index";
import { FormikProps, FormikValues } from "formik";
import { Button, Collapse, Progress } from "reactstrap";

export type ProfileCompletenessSection = {
    id: string;
    label: string;
    anchorId: string;
    applicable: boolean;
    complete: boolean;
};

function hasStr(v: unknown): boolean {
    return typeof v === "string" && v.trim().length > 0;
}

function hasDate(v: unknown): boolean {
    return v != null && Object.prototype.toString.call(v) === "[object Date]" && !isNaN((v as Date).getTime());
}

function isAffirmativeCheckbox(v: unknown): boolean {
    return v === true;
}

function mccqePassIsTrue(v: UserProfile["mccqePart1Pass"]): boolean {
    return v === true || (v as unknown)?.toString?.() === "true";
}

function nacRetakeIsTrue(v: UserProfile["nacRetakeFall"]): boolean {
    return v === true || (v as unknown)?.toString?.() === "true";
}

function mccqePassIsFalse(v: UserProfile["mccqePart1Pass"]): boolean {
    return v === false || (v as unknown)?.toString?.() === "false";
}

function nacExamStrictTrue(v: UserProfile["nacExam"]): boolean {
    return v === true;
}

/** Yup-required fields only (see `UserProfile.ValidationSchema`). Section "agree" checkboxes are UI-only. */
export function getUserProfileCompletenessSections(v: UserProfile): ProfileCompletenessSection[] {
    const sections: ProfileCompletenessSection[] = [];

    const prelimComplete = isAffirmativeCheckbox(v.acknowledgeEmailResponsibility);
    sections.push({
        id: "preliminaries",
        label: "Email responsibility",
        anchorId: "user-profile-section-preliminaries",
        applicable: true,
        complete: prelimComplete,
    });

    const personalComplete =
        hasStr(v.surname) &&
        hasStr(v.givenNames) &&
        hasDate(v.dateOfBirth) &&
        hasStr(v.gender);
    sections.push({
        id: "personalBackground",
        label: "Personal background",
        anchorId: "user-profile-section-personal-background",
        applicable: true,
        complete: personalComplete,
    });

    const physApplyComplete =
        hasStr(v.physiciansApply) &&
        (v.physiciansApply !== "No" ||
            (hasStr(v.physiciansApplySurname) && hasStr(v.physiciansApplyGivenNames)));
    sections.push({
        id: "physiciansApply",
        label: "physiciansapply.ca name",
        anchorId: "user-profile-section-physicians-apply",
        applicable: true,
        complete: physApplyComplete,
    });

    const currentAddrComplete =
        hasStr(v.currentStreetAddress) &&
        hasStr(v.currentTownCity) &&
        hasStr(v.currentProvince) &&
        hasStr(v.currentPostalCode) &&
        hasStr(v.currentCountry) &&
        hasStr(v.telephone);
    sections.push({
        id: "currentAddress",
        label: "Current address",
        anchorId: "user-profile-section-current-address",
        applicable: true,
        complete: currentAddrComplete,
    });

    const mailingComplete =
        hasStr(v.sameAsMailAddress) &&
        (v.sameAsMailAddress === "Yes" ||
            (hasStr(v.mailStreetAddress) &&
                hasStr(v.mailCity) &&
                hasStr(v.mailProvince) &&
                hasStr(v.mailPostalCode) &&
                hasStr(v.mailCountry)));
    sections.push({
        id: "mailingAddress",
        label: "Mailing address",
        anchorId: "user-profile-section-mailing-address",
        applicable: true,
        complete: mailingComplete,
    });

    let addressHistoryComplete = hasDate(v.currentAddressMoveInDate);
    if (v.hasPreviousAddress1) {
        addressHistoryComplete =
            addressHistoryComplete &&
            hasStr(v.previousStreetAddress1) &&
            hasStr(v.previousCity1) &&
            hasStr(v.previousProvince1) &&
            hasDate(v.previousMoveInDate1) &&
            hasDate(v.previousMoveOutDate1);
    }
    if (v.hasPreviousAddress2) {
        addressHistoryComplete =
            addressHistoryComplete &&
            hasStr(v.previousStreetAddress2) &&
            hasStr(v.previousCity2) &&
            hasStr(v.previousProvince2) &&
            hasDate(v.previousMoveInDate2) &&
            hasDate(v.previousMoveOutDate2);
    }
    if (v.hasPreviousAddress3) {
        addressHistoryComplete =
            addressHistoryComplete &&
            hasStr(v.previousStreetAddress3) &&
            hasStr(v.previousCity3) &&
            hasStr(v.previousProvince3) &&
            hasDate(v.previousMoveInDate3) &&
            hasDate(v.previousMoveOutDate3);
    }
    sections.push({
        id: "addressHistory",
        label: "Residency / address history",
        anchorId: "user-profile-section-address-history",
        applicable: true,
        complete: addressHistoryComplete,
    });

    const supportingComplete = hasStr(v.applicantIdentityDeclaration);
    sections.push({
        id: "supportingDocs",
        label: "Supporting documents declaration",
        anchorId: "user-profile-section-supporting-docs",
        applicable: true,
        complete: supportingComplete,
    });

    const medicalComplete =
        hasStr(v.medicalDegreeCountry) &&
        hasStr(v.universityName) &&
        hasStr(v.medicalSchoolGraduationYear) &&
        hasStr(v.medicalSchoolGraduationMonth);
    sections.push({
        id: "medicalDegree",
        label: "Medical degree",
        anchorId: "user-profile-section-medical-degree",
        applicable: true,
        complete: medicalComplete,
    });

    const statusComplete = hasStr(v.statusInCanada) && hasStr(v.albertaResidencyType);
    sections.push({
        id: "statusCanada",
        label: "Status in Canada / Alberta residency",
        anchorId: "user-profile-section-status-canada",
        applicable: true,
        complete: statusComplete,
    });

    const elpComplete = hasStr(v.englishLanguageProficiency);
    sections.push({
        id: "elp",
        label: "English language proficiency",
        anchorId: "user-profile-section-elp",
        applicable: true,
        complete: elpComplete,
    });

    const cpsaApplicable = v.englishLanguageProficiency === "CPSA";
    let cpsaComplete = false;
    if (cpsaApplicable) {
        const needsLicense = v.cpsaEvidence === "license";
        cpsaComplete = !needsLicense || hasStr(v.cpsaLicenseNumber);
    }
    sections.push({
        id: "cpsa",
        label: "CPSA evidence",
        anchorId: "user-profile-section-cpsa",
        applicable: cpsaApplicable,
        complete: cpsaApplicable ? cpsaComplete : true,
    });

    const ieltsApplicable = v.englishLanguageProficiency === "IELTS";
    let ieltsComplete = false;
    if (ieltsApplicable) {
        const lic = v.cpsaLicense || "";
        const licenseKindRequired =
            ["IELTS", "OET", "CELPIP"].indexOf(lic) >= 0;
        ieltsComplete = hasStr(lic) && (!licenseKindRequired || hasStr(v.cpsaStatus));
    }
    sections.push({
        id: "ielts",
        label: "ELP exam (IELTS / OET / CELPIP)",
        anchorId: "user-profile-section-ielts",
        applicable: ieltsApplicable,
        complete: ieltsApplicable ? ieltsComplete : true,
    });

    const mccComplete = hasStr(v.mccCandidateCode);
    sections.push({
        id: "mcc",
        label: "MCC / CaRMS identifiers",
        anchorId: "user-profile-section-mcc",
        applicable: true,
        complete: mccComplete,
    });

    const pass = mccqePassIsTrue(v.mccqePart1Pass);
    const fail = mccqePassIsFalse(v.mccqePart1Pass);
    let mccqeComplete = false;
    if (pass) {
        mccqeComplete = hasDate(v.mccqePart1DateTaken) && hasStr(v.mccqePart1Score);
    } else if (fail) {
        const reg = v.mccqePart1Registered;
        const regOk = hasStr(reg);
        const intendOk = reg !== "No" || isAffirmativeCheckbox(v.mccqePart1RegisterIntend);
        mccqeComplete = regOk && intendOk;
    } else {
        mccqeComplete = true;
    }
    sections.push({
        id: "mccqe",
        label: "MCCQE Part 1",
        anchorId: "user-profile-section-mccqe",
        applicable: true,
        complete: mccqeComplete,
    });

    const nacPassStrict = nacExamStrictTrue(v.nacExam);
    const nacScoreNumericOk =
        hasStr(v.nacExamScore) && !Number.isNaN(Number(String(v.nacExamScore).trim()));
    let nacComplete = false;
    if (nacPassStrict) {
        const scoreOk = v.nacExamScoreType !== "score" || nacScoreNumericOk;
        nacComplete = hasDate(v.nacExamDateTaken) && scoreOk;
    } else {
        const retake = nacRetakeIsTrue(v.nacRetakeFall);
        const needsRegisterIntend = v.nacExam !== true && !retake;
        const intendOk = !needsRegisterIntend || isAffirmativeCheckbox(v.nacRegisterIntend);
        nacComplete = intendOk;
    }
    sections.push({
        id: "nac",
        label: "NAC exam",
        anchorId: "user-profile-section-nac",
        applicable: true,
        complete: nacComplete,
    });

    return sections;
}

export function getProfileCompletenessPercent(sections: ProfileCompletenessSection[]): number {
    const applicable = sections.filter((s) => s.applicable);
    if (applicable.length === 0) {
        return 0;
    }
    const done = applicable.filter((s) => s.complete).length;
    return Math.round((done / applicable.length) * 100);
}

type TrackerProps = {
    formik: FormikProps<FormikValues>;
};

export function UserProfileCompletenessTracker(props: TrackerProps) {
    const values = props.formik.values as UserProfile;
    const [detailsOpen, setDetailsOpen] = React.useState(false);

    const sections = React.useMemo(() => getUserProfileCompletenessSections(values), [values]);
    const applicable = sections.filter((s) => s.applicable);
    const percent = getProfileCompletenessPercent(sections);
    const doneCount = applicable.filter((s) => s.complete).length;

    const scrollTo = (anchorId: string) => {
        const el = document.getElementById(anchorId);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <>
            <style>
                {`[id^="user-profile-section-"] { scroll-margin-top: 6.5rem; }`}
            </style>
            <div
                className="user-profile-completeness-tracker border-bottom shadow-sm bg-white"
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1020,
                    fontSize: "30px",
                    padding: "10px"
                }}
            >
                <div className="container-fluid py-2 px-3">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="fw-semibold small text-nowrap">Required fields</span>
                        <div className="flex-grow-1" style={{ minWidth: "120px" }}>
                            <Progress value={percent} className="mb-0" style={{ height: "10px" }} />
                        </div>
                        <span className="small text-muted text-nowrap">
                            {doneCount}/{applicable.length} sections ({percent}%)
                        </span>
                        <Button
                            color="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            type="button"
                            onClick={() => setDetailsOpen((o) => !o)}
                        >
                            {detailsOpen ? "Hide" : "Show"} sections
                        </Button>
                    </div>
                    <Collapse isOpen={detailsOpen}>
                        <ul className="list-unstyled small mb-0 mt-2 ps-1">
                            {applicable.map((s) => (
                                <li key={s.id} className="mb-1">
                                    <button
                                        type="button"
                                        className={`btn btn-link p-0 text-start text-decoration-none ${
                                            s.complete ? "text-success" : "text-secondary"
                                        }`}
                                        onClick={() => scrollTo(s.anchorId)}
                                    >
                                        {s.complete ? "✓ " : "○ "}
                                        {s.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </Collapse>
                </div>
            </div>
        </>
    );
}
